import React, { useEffect, useState, useCallback } from "react";
import { Container, Button, Nav, Alert } from "react-bootstrap";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../services/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getCountFromServer,
  onSnapshot,
} from "firebase/firestore";
import "./dashboard.css";
import Chat from "../../components/Chat/Chat";
import {
  BsGrid,
  BsChat,
  BsBoxArrowRight,
  BsList,
  BsX,
  BsHouseDoor,
  BsPersonCheck,
  BsChatDots,
  BsPeople,
  BsCheckCircle,
  BsClock,
} from "react-icons/bs";
import TimeshareList from "../../components/TimeshareList/TimeshareList";
import TimeshareDetail from "../../components/TimeshareList/TimeshareDetail";
import Consultations from "../../components/Consultations/Consultations";
import { useSidebar } from "../../contexts/SidebarContext";

// Loading Screen Component
const LoadingScreen = () => (
  <div className="loading-screen">
    <div className="loading-content">
      <div className="loading-logo">
        <div className="spinning-logo">
          <img
            src="/logo.svg"
            alt="Logo"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
      <div className="loading-text">
        <h4>Loading...</h4>
        <p>Please wait while we load your data</p>
      </div>
      <div className="loading-spinner">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </div>
);

// Error Screen Component
const ErrorScreen = ({ error }) => (
  <div className="error-screen">
    <Alert variant="danger" className="error-alert">
      <Alert.Heading>Oops! Something went wrong</Alert.Heading>
      <p>{error}</p>
      <Button variant="outline-danger" onClick={() => window.location.reload()}>
        Try Again
      </Button>
    </Alert>
  </div>
);

// Dashboard Overview Component
const DashboardOverview = ({ user, isAdmin, stats, handleTabChange }) => (
  <div className="dashboard-overview">
    {/* Welcome Section */}
    <div className="welcome-section">
      <div className="welcome-content">
        <div className="welcome-text">
          <h1 className="welcome-title">
            Welcome back, {user?.firstName || "User"}! 👋
          </h1>
          <p className="welcome-subtitle">
            {isAdmin
              ? "Manage your consultations and client communications efficiently"
              : "Track your timeshare progress and get support when needed"}
          </p>
        </div>
        <div className="welcome-illustration">
          <div className="illustration-circle">
            <BsGrid className="illustration-icon" />
          </div>
        </div>
      </div>
    </div>

    {/* Quick Actions */}
    <div className="quick-actions">
      <h2 className="section-title">Quick Actions</h2>
      <div className="action-cards">
        {isAdmin ? (
          <>
            <div
              className="action-card"
              onClick={() => handleTabChange("consultations")}
            >
              <div className="action-icon consultations">
                <BsPersonCheck />
              </div>
              <h5>Manage Consultations</h5>
              <p>Review and process client consultation requests</p>
            </div>
            <div
              className="action-card"
              onClick={() => handleTabChange("chat")}
            >
              <div className="action-icon messages">
                <BsChatDots />
              </div>
              <h5>Messages</h5>
              <p>Communicate directly with clients</p>
            </div>
            {/* Analytics/Reports card removed */}
          </>
        ) : (
          <>
            <div
              className="action-card"
              onClick={() => handleTabChange("timeshares")}
            >
              <div className="action-icon timeshares">
                <BsHouseDoor />
              </div>
              <h5>My Timeshares</h5>
              <p>View and manage your timeshare contracts</p>
            </div>
            <div
              className="action-card"
              onClick={() => handleTabChange("chat")}
            >
              <div className="action-icon support">
                <BsChatDots />
              </div>
              <h5>Support Chat</h5>
              <p>Get help from our support team</p>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Modern System Overview Section */}
    <div className="statistics-section modern-system-overview">
      <div className="system-overview-header">
        <h2 className="system-overview-title">
          {isAdmin ? "System Overview" : "Your Summary"}
        </h2>
        <div className="system-overview-bar" />
      </div>
      <div className="modern-stats-grid">
        {isAdmin ? (
          <>
            <div className="modern-stat-card">
              <div className="modern-stat-icon users">
                <BsPeople />
              </div>
              <div className="modern-stat-content">
                <div className="modern-stat-number">
                  {stats.totalUsers || 0}
                </div>
                <div className="modern-stat-label">Total Users</div>
                <div className="modern-stat-sublabel">Registered clients</div>
              </div>
            </div>
            <div className="modern-stat-card">
              <div className="modern-stat-icon consultations">
                <BsPersonCheck />
              </div>
              <div className="modern-stat-content">
                <div className="modern-stat-number">
                  {stats.totalConsultations || 0}
                </div>
                <div className="modern-stat-label">Consultations</div>
                <div className="modern-stat-sublabel">Total requests</div>
              </div>
            </div>
            <div className="modern-stat-card">
              <div className="modern-stat-icon completed">
                <BsCheckCircle />
              </div>
              <div className="modern-stat-content">
                <div className="modern-stat-number">
                  {stats.completedConsultations || 0}
                </div>
                <div className="modern-stat-label">Completed</div>
                <div className="modern-stat-sublabel">
                  Finished consultations
                </div>
              </div>
            </div>
            <div className="modern-stat-card">
              <div className="modern-stat-icon pending">
                <BsClock />
              </div>
              <div className="modern-stat-content">
                <div className="modern-stat-number">
                  {stats.incompleteConsultations || 0}
                </div>
                <div className="modern-stat-label">Pending</div>
                <div className="modern-stat-sublabel">Awaiting review</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="modern-stat-card">
              <div className="modern-stat-icon timeshares">
                <BsHouseDoor />
              </div>
              <div className="modern-stat-content">
                <div className="modern-stat-number">
                  {stats.totalTimeshares || 0}
                </div>
                <div className="modern-stat-label">Timeshares</div>
                <div className="modern-stat-sublabel">Your contracts</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

// Dashboard Layout Component that provides sidebar functionality
const DashboardLayout = ({
  children,
  user,
  isAdmin,
  handleLogout,
  handleTabChange,
  getActiveTab,
  stats,
  unreadMessageCount,
}) => {
  const { showSidebar, toggleSidebar, closeSidebar } = useSidebar();
  const isChatActive = getActiveTab() === "chat";

  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSidebar();
  };

  return (
    <div
      className={`dashboard ${showSidebar ? "sidebar-open show-sidebar" : ""} ${
        window.innerWidth >= 992 ? "desktop-view" : ""
      } ${isChatActive ? "chat-fullscreen" : ""}`}
    >
      {/* Mobile Overlay */}
      <div
        className={`mobile-overlay ${showSidebar ? "" : "hidden"}`}
        onClick={closeSidebar}
        style={{ display: window.innerWidth >= 992 ? "none" : "block" }}
      />

      {/* Toggle Button Only */}
      <div className="toggle-button-container">
        <Button
          variant="link"
          className="header-toggle"
          onClick={handleToggleClick}
          type="button"
          data-testid="sidebar-toggle"
        >
          <BsList />
        </Button>
      </div>

      {/* Sidebar */}
      <div className={`dashboard-sidebar ${showSidebar ? "show" : ""}`}>
        <div className="dashboard-sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.svg" alt="Logo" className="logo-icon" />
            <span className="logo-text">
              {isAdmin ? "Admin Dashboard" : "Client Portal"}
            </span>
          </div>
          <Button
            variant="link"
            className="sidebar-close d-md-none"
            onClick={closeSidebar}
          >
            <BsX />
          </Button>
        </div>

        <Nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-header">
              <span className="nav-section-title">
                {isAdmin ? "Admin Panel" : "Client Portal"}
              </span>
            </div>

            {/* Dashboard Home */}
            <div
              className={`nav-item ${
                getActiveTab() === "dashboard" ? "active" : ""
              }`}
              onClick={() => handleTabChange("dashboard")}
            >
              <div className="nav-content">
                <BsGrid className="nav-icon" />
                <span className="nav-text">Dashboard</span>
              </div>
            </div>

            {isAdmin ? (
              <>
                <div
                  className={`nav-item ${
                    getActiveTab() === "consultations" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange("consultations")}
                >
                  <div className="nav-content">
                    <BsPersonCheck className="nav-icon" />
                    <span className="nav-text">Consultations</span>
                  </div>
                </div>

                <div
                  className={`nav-item ${
                    getActiveTab() === "chat" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange("chat")}
                >
                  <div className="nav-content">
                    <BsChat className="nav-icon" />
                    <span className="nav-text">Messages</span>
                    {unreadMessageCount > 0 && (
                      <span className="unread-count-badge">
                        {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className={`nav-item ${
                    getActiveTab() === "timeshares" ||
                    getActiveTab() === "timeshare-detail"
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleTabChange("timeshares")}
                >
                  <div className="nav-content">
                    <BsHouseDoor className="nav-icon" />
                    <span className="nav-text">My Timeshares</span>
                  </div>
                </div>

                <div
                  className={`nav-item ${
                    getActiveTab() === "chat" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange("chat")}
                >
                  <div className="nav-content">
                    <BsChat className="nav-icon" />
                    <span className="nav-text">Support</span>
                    {unreadMessageCount > 0 && (
                      <span className="unread-count-badge">
                        {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Section */}
          <div className="nav-section">
            <div className="nav-section-header">
              <span className="nav-section-title">Account</span>
            </div>

            <div className="nav-item" onClick={handleLogout}>
              <div className="nav-content">
                <BsBoxArrowRight className="nav-icon" />
                <span className="nav-text">Sign Out</span>
              </div>
            </div>
          </div>
        </Nav>
      </div>

      {/* Dashboard Content */}
      <div className="dashboard-content">
        <Container fluid>
          {/* Tab Content */}
          <div className="tab-content">
            <div
              className={`tab-pane fade ${
                getActiveTab() === "dashboard" ? "show active" : ""
              }`}
              id="dashboard-tab"
            >
              <div className="fade-in">
                <DashboardOverview
                  user={user}
                  isAdmin={isAdmin}
                  stats={stats}
                  handleTabChange={handleTabChange}
                />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "consultations" ? "show active" : ""
              }`}
              id="consultations-tab"
            >
              <div className="fade-in">
                <Consultations />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "timeshares" ? "show active" : ""
              }`}
              id="timeshares-tab"
            >
              <div className="fade-in">
                <TimeshareList uid={user?.uid} />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "timeshare-detail" ? "show active" : ""
              }`}
              id="timeshare-detail-tab"
            >
              <div className="fade-in">
                <TimeshareDetail />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "chat" ? "show active" : ""
              }`}
              id="chat-tab"
            >
              <div className="chat-container with-sidebar dashboard">
                <Chat />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");

  const [stats, setStats] = useState({
    // Admin stats
    totalUsers: 0,
    totalConsultations: 0,
    completedConsultations: 0,
    incompleteConsultations: 0,
    completionRate: 0,
    thisMonthConsultations: 0,
    // User stats
    totalTimeshares: 0,
    userConsultations: 0,
    completedUserConsultations: 0,
    pendingUserConsultations: 0,
  });

  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // Load unread message count
  const loadUnreadMessageCount = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const unreadQuery = query(
        collection(db, "messages"),
        where("receiver", "==", user.uid),
        where("read", "==", false)
      );

      const countSnapshot = await getCountFromServer(unreadQuery);
      setUnreadMessageCount(countSnapshot.data().count);
    } catch (error) {
      console.error("Error loading unread message count:", error);
    }
  }, [user?.uid]);

  // Real-time listener for unread messages
  useEffect(() => {
    if (!user?.uid) return;

    const unreadQuery = query(
      collection(db, "messages"),
      where("receiver", "==", user.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(
      unreadQuery,
      (snapshot) => {
        setUnreadMessageCount(snapshot.docs.length);
      },
      (error) => {
        console.error("Error listening to unread messages:", error);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Load unread count on user change
  useEffect(() => {
    if (user?.uid) {
      loadUnreadMessageCount();
    }
  }, [user?.uid, loadUnreadMessageCount]);

  // Fetch dashboard statistics using getCountFromServer to avoid reading documents
  const fetchStats = useCallback(async () => {
    if (!user?.uid) {
      setStatsLoading(false);
      return;
    }

    try {
      const consultationsRef = collection(db, "consultations");
      const usersRef = collection(db, "users");
      const timesharesRef = collection(db, "timeshares");

      if (isAdmin) {
        // Admin statistics
        const [
          totalUsersSnapshot,
          completedSnapshot,
          incompleteSnapshot,
          thisMonthSnapshot,
        ] = await Promise.all([
          getCountFromServer(usersRef),
          getCountFromServer(
            query(consultationsRef, where("completed", "==", true))
          ),
          getCountFromServer(
            query(consultationsRef, where("completed", "==", false))
          ),
          getCountFromServer(
            query(
              consultationsRef,
              where(
                "createdAt",
                ">=",
                new Date(new Date().getFullYear(), new Date().getMonth(), 1)
              ),
              where(
                "createdAt",
                "<=",
                new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
              )
            )
          ),
        ]);

        // Extract admin counts from snapshots
        const totalUsers = Math.max(0, totalUsersSnapshot.data().count - 1); // Subtract admin
        const completedConsultations = completedSnapshot.data().count;
        const incompleteConsultations = incompleteSnapshot.data().count;
        const totalConsultations =
          completedConsultations + incompleteConsultations;
        const thisMonthConsultations = thisMonthSnapshot.data().count;

        const completionRate =
          totalConsultations > 0
            ? Math.round((completedConsultations / totalConsultations) * 100)
            : 0;

        setStats({
          totalUsers,
          totalConsultations,
          completedConsultations,
          incompleteConsultations,
          completionRate,
          thisMonthConsultations,
          // Reset user stats for admin
          totalTimeshares: 0,
          userConsultations: 0,
          completedUserConsultations: 0,
          pendingUserConsultations: 0,
        });
      } else {
        // User statistics
        const [
          totalTimesharesSnapshot,
          userConsultationsSnapshot,
          completedUserConsultationsSnapshot,
          pendingUserConsultationsSnapshot,
        ] = await Promise.all([
          getCountFromServer(
            query(timesharesRef, where("uid", "==", user.uid))
          ),
          getCountFromServer(
            query(consultationsRef, where("uid", "==", user.uid))
          ),
          getCountFromServer(
            query(
              consultationsRef,
              where("uid", "==", user.uid),
              where("completed", "==", true)
            )
          ),
          getCountFromServer(
            query(
              consultationsRef,
              where("uid", "==", user.uid),
              where("completed", "==", false)
            )
          ),
        ]);

        // Extract user counts from snapshots
        const totalTimeshares = totalTimesharesSnapshot.data().count;
        const userConsultations = userConsultationsSnapshot.data().count;
        const completedUserConsultations =
          completedUserConsultationsSnapshot.data().count;
        const pendingUserConsultations =
          pendingUserConsultationsSnapshot.data().count;

        setStats({
          // Reset admin stats for user
          totalUsers: 0,
          totalConsultations: 0,
          completedConsultations: 0,
          incompleteConsultations: 0,
          completionRate: 0,
          thisMonthConsultations: 0,
          // User stats
          totalTimeshares,
          userConsultations,
          completedUserConsultations,
          pendingUserConsultations,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [isAdmin, user?.uid]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      setError("");

      try {
        if (currentUser) {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = { uid: userDocSnap.id, ...userDocSnap.data() };
            setUser(userData);
            const adminStatus = userData.isAdmin || false;
            setIsAdmin(adminStatus);

            // Fetch stats for both admin and regular users
            await fetchStats();
          } else {
            setError("User data not found");
          }
        } else {
          navigate("/SignIn");
        }
      } catch (err) {
        setError("Failed to load user data. Please try again.");
        console.error("Auth error:", err);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    });

    return () => unsubscribe();
  }, [navigate, fetchStats]);

  // Use sidebar context
  const { closeSidebar } = useSidebar();

  const handleLogout = () => {
    setLoading(true);
    auth.signOut();
    navigate("/SignIn");
  };

  // Get active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname.split("/");

    // Check if we're on a timeshare detail page
    if (path.includes("timeshares") && path.length > 3) {
      return "timeshare-detail";
    }

    // Handle timeshares list route
    if (path.includes("timeshares") && path.length === 3) {
      return "timeshares";
    }

    // Check if we're on a chat with user page
    if (path.includes("chat") && path.length > 3) {
      return "chat";
    }

    return path[path.length - 1] === "dashboard"
      ? "dashboard"
      : path[path.length - 1] || "dashboard";
  };

  const handleTabChange = (tab) => {
    if (tab === "dashboard") {
      navigate("/dashboard");
    } else {
      navigate(`/dashboard/${tab}`);
    }
    // Close sidebar on mobile after selecting a tab
    if (window.innerWidth <= 768) {
      closeSidebar();
    }
  };

  // Show loading screen while either main data or stats are loading
  if (loading || statsLoading) {
    return <LoadingScreen />;
  }

  // Show error screen
  if (error && !user) {
    return <ErrorScreen error={error} />;
  }

  // Show nothing if no user (will redirect)
  if (!user) {
    return null;
  }

  // Check if chat is active to apply full-screen layout
  const isChatActive =
    getActiveTab() === "chat" || getActiveTab() === "chat-with-user";

  // Check if we're on a nested route (like timeshare detail or chat with user)
  const isNestedRoute =
    (location.pathname.includes("/timeshares/") &&
      location.pathname.split("/").length > 3) ||
    (location.pathname.includes("/chat/") &&
      location.pathname.split("/").length > 3);

  return (
    <DashboardLayout
      user={user}
      isAdmin={isAdmin}
      handleLogout={handleLogout}
      handleTabChange={handleTabChange}
      getActiveTab={getActiveTab}
      stats={stats}
      unreadMessageCount={unreadMessageCount}
    >
      {isNestedRoute ? (
        // Render nested routes (like TimeshareDetail)
        <Outlet />
      ) : isChatActive ? (
        // Full-screen chat layout
        <div className="chat-fullscreen-container">
          <div className="chat-container chat-fullscreen dashboard">
            <Chat />
          </div>
        </div>
      ) : (
        // Regular dashboard layout
        <Container fluid className="content-container">
          {/* Tab Content */}
          <div className="tab-content">
            <div
              className={`tab-pane fade ${
                getActiveTab() === "dashboard" ? "show active" : ""
              }`}
              id="dashboard-tab"
            >
              <div className="fade-in">
                <DashboardOverview
                  user={user}
                  isAdmin={isAdmin}
                  stats={stats}
                  handleTabChange={handleTabChange}
                />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "consultations" ? "show active" : ""
              }`}
              id="consultations-tab"
            >
              <div className="fade-in">
                <Consultations />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "timeshares" ? "show active" : ""
              }`}
              id="timeshares-tab"
            >
              <div className="fade-in">
                <TimeshareList uid={user?.uid} />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "timeshare-detail" ? "show active" : ""
              }`}
              id="timeshare-detail-tab"
            >
              <div className="fade-in">
                <TimeshareDetail />
              </div>
            </div>

            <div
              className={`tab-pane fade ${
                getActiveTab() === "chat" ? "show active" : ""
              }`}
              id="chat-tab"
            >
              <Chat />
            </div>
          </div>
        </Container>
      )}
    </DashboardLayout>
  );
}

export { DashboardLayout };
export default Dashboard;
